// The bundle page's pure logic: which bookings are past and which upcoming,
// how many spots the bundle has left, what it cost, and how dates read. A
// port of v1's ManageBundleView, BundleInformation, UpcomingBookings and
// PastBookings, on Intl rather than moment and in 24-hour time.

import type { BundleBooking, BundleDoc, ManageLocation, ManageTutor } from "./types";
import { googleCalendarLink } from "../../calendar";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** When a booking takes place (a rescheduled one's new time first). */
export function bookingDate(booking: BundleBooking): string | undefined {
  return booking.currentStartDateTime || booking.session?.startDateTime || undefined;
}

const time = (iso: string | undefined) => (iso ? new Date(iso).getTime() : NaN);

/** The bookings still to come (soonest first, undated last) and the ones
 * that have happened (in the order given: the API's newest first). */
export function splitBookings(bookings: BundleBooking[], now: number) {
  const upcoming: BundleBooking[] = [];
  const past: BundleBooking[] = [];
  for (const booking of bookings) {
    const at = time(bookingDate(booking));
    if (Number.isFinite(at) && at < now) past.push(booking);
    else upcoming.push(booking);
  }
  upcoming.sort((a, b) => {
    const da = time(bookingDate(a));
    const db = time(bookingDate(b));
    if (!Number.isFinite(da) && !Number.isFinite(db)) return 0;
    if (!Number.isFinite(da)) return 1;
    if (!Number.isFinite(db)) return -1;
    return da - db;
  });
  return { upcoming, past };
}

/** How many tickets a booking holds. */
export function ticketCount(booking: Pick<BundleBooking, "arrayTickets" | "tickets" | "quantity">): number {
  const list = Array.isArray(booking.arrayTickets) ? booking.arrayTickets : Array.isArray(booking.tickets) ? booking.tickets : null;
  if (list) return list.reduce((sum, t) => sum + (t.count || t.quantity || 1), 0);
  return booking.quantity || 1;
}

/** Whether a booking, or its session, was cancelled. */
export const isBookingCancelled = (booking: BundleBooking) => Boolean(booking.cancelled || booking.session?.cancelled);

/** "In 5 minutes", "In 3 hours", "Tomorrow", "In 4 days", else the date. */
export function relativeTime(iso: string, now: number, timezone: string): string {
  const diff = time(iso) - now;
  const minutes = Math.trunc(diff / MINUTE);
  const hours = Math.trunc(diff / HOUR);
  const days = Math.trunc(diff / DAY);
  if (minutes <= 0) return "Starting now";
  if (minutes < 60) return `In ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  if (hours < 24) return `In ${hours} ${hours === 1 ? "hour" : "hours"}`;
  if (days === 1) return "Tomorrow";
  if (days < 7) return `In ${days} days`;
  return formatMonthDay(iso, timezone);
}

/** Within the next 24 hours. */
export function isStartingSoon(iso: string, now: number): boolean {
  const hours = Math.trunc((time(iso) - now) / HOUR);
  return hours >= 0 && hours <= 24;
}

function parts(iso: string, timezone: string, options: Intl.DateTimeFormatOptions) {
  const out: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat("en-US", { timeZone: timezone, ...options }).formatToParts(new Date(iso))) {
    out[part.type] = part.value;
  }
  return out;
}

/** "Oct 05, 2026" */
export function formatDay(iso: string | undefined | null, timezone: string): string {
  if (!iso || !Number.isFinite(time(iso))) return "";
  const p = parts(iso, timezone, { month: "short", day: "2-digit", year: "numeric" });
  return `${p.month} ${p.day}, ${p.year}`;
}

/** "Oct 05" */
export function formatMonthDay(iso: string, timezone: string): string {
  const p = parts(iso, timezone, { month: "short", day: "2-digit" });
  return `${p.month} ${p.day}`;
}

/** "Oct 05, 2026 at 09:00" (24-hour). */
export function formatDateTime(iso: string, timezone: string): string {
  const p = parts(iso, timezone, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return `${p.month} ${p.day}, ${p.year} at ${p.hour}:${p.minute}`;
}

/** A location's name for a row (v1: its area, else its name). */
export function locationName(locations: ManageLocation[] | undefined, id: string | undefined): string | undefined {
  if (!id) return undefined;
  const location = locations?.find((l) => l._id === id);
  return location?.area || location?.name || undefined;
}

/** A tutor's full name. */
export function tutorName(tutors: ManageTutor[] | undefined, id: string | undefined): string | undefined {
  if (!id) return undefined;
  const tutor = tutors?.find((t) => t._id === id);
  const name = `${tutor?.firstName ?? ""} ${tutor?.lastName ?? ""}`.trim();
  return name || undefined;
}

/** A Google Calendar "add event" link for a booking. */
export function calendarUrl(booking: BundleBooking, location: string | undefined): string | undefined {
  const start = bookingDate(booking);
  if (!start) return undefined;
  return googleCalendarLink({ title: booking.course?.title || "Session", start, duration: booking.course?.duration || 60, location });
}

export type BundleState = {
  cancelled: boolean;
  refunded: boolean;
  expired: boolean;
  /** No new bookings can be made with it. */
  closed: boolean;
};

export function bundleState(bundle: BundleDoc, now: number): BundleState {
  const cancelled = bundle.status === "disabled";
  const refunded = bundle.refunded === true;
  const expiry = time(bundle.expiredDate);
  const expired = Number.isFinite(expiry) && expiry < now;
  return { cancelled, refunded, expired, closed: cancelled || expired };
}

export type Spots = {
  total: number;
  used: number;
  /** Left once the bookings still being made are counted. */
  remaining: number;
  /** Left before them. */
  available: number;
  pending: number;
};

/** The bundle's spots: total, used, being booked, and left. */
export function bundleSpots(bundle: BundleDoc, pending = 0): Spots {
  const total = bundle.totalSessions || 0;
  const used = bundle.usedSessions || 0;
  const available = total - used;
  return { total, used, available, pending, remaining: available - pending };
}

/** A 0–100 width. */
export const percent = (part: number, whole: number) => (whole > 0 ? Math.max(0, Math.min(100, (part / whole) * 100)) : 0);

export type BundlePrice = {
  paid: number;
  original: number;
  discount: number;
  /** "Price Paid" (a discount took something off) or "Purchase Price". */
  label: string;
  /** The VAT rate to show, as a percentage, when the price includes VAT. */
  vatPercent?: number;
};

/** What the bundle cost: its accounting when it has one, else its price and
 * VAT. Pence. `vat` is a rate (0.2) on some bundles and a percentage (20)
 * on others; both read the same here. */
export function bundlePrice(bundle: BundleDoc): BundlePrice {
  const vat = bundle.vat || 0;
  const rate = vat > 1 ? vat / 100 : vat;
  const vatPercent = bundle.isVatIncluded && vat > 0 ? Math.round(rate * 1000) / 10 : undefined;
  const hasDiscount = (bundle.discount?.value ?? 0) > 0;
  if (bundle.accounting && typeof bundle.accounting === "object") {
    const paid = bundle.accounting.userCharged ?? 0;
    const original = bundle.accounting.rawEarning ?? paid;
    const discount = bundle.accounting.discountAmountUsed || 0;
    return { paid, original, discount, label: hasDiscount && discount > 0 ? "Price Paid" : "Purchase Price", vatPercent };
  }
  const price = bundle.price || 0;
  const paid = bundle.isVatIncluded ? price : Math.round(price + price * rate);
  return { paid, original: paid, discount: 0, label: "Purchase Price", vatPercent };
}

/** "20% off", or an amount (formatted by the caller). */
export function discountLabel(discount: NonNullable<BundleDoc["discount"]>, money: (pence: number) => string): string {
  return discount.type === "PERCENTAGE" ? `${discount.value}% off` : money(discount.value ?? 0);
}
